import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Accordion, AccordionSummary, AccordionDetails, useMediaQuery, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import { Helmet } from 'react-helmet';
import 'highlight.js/styles/github.css'; // Use a GitHub-like syntax highlighting theme
import { useTheme } from '@mui/material/styles';
import rehypeRaw from "rehype-raw";

const Changelog = () => {
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const fetchReleases = async () => {
            try {
                const response = await fetch('https://api.github.com/repos/prashantrathi123/hawkClient/releases');
                if (!response.ok) {
                    throw new Error('Failed to fetch releases');
                }
                const data = await response.json();
                setReleases(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchReleases();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', padding: 3 }}>
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Helmet>
                <title>Changelog - HawkClient</title>
                <meta
                    name="description"
                    content="View the latest release updates and changelog for HawkClient, a developer-friendly offline-only API client."
                />
            </Helmet>
            <Box sx={{ pl: isMediumScreen ? 3 : 20, pr: isMediumScreen ? 3 : 20, pt: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Changelog
                </Typography>
                {releases.length === 0 ? (
                    <Typography>No releases found.</Typography>
                ) : (
                    releases.map((release) => (
                        <Accordion key={release.id} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontSize: '24px', fontWeight: 'bold', color: '#D4AF37' }}>
                                        {release.name || release.tag_name}
                                    </Typography>
                                    <Typography>
                                        {new Date(release.published_at).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: isMobile ? '0px' : '130px', borderLeft: isMobile ? '0px' : '1px solid #D4AF37', paddingLeft: isMobile ? '0px' : '24px' }}>
                                    {release.body ? (
                                        <ReactMarkdown
                                            rehypePlugins={[rehypeRaw, rehypeHighlight]}
                                            components={{
                                                p: ({ node, ...props }) => <Typography {...props} sx={{ mb: 1 }} />,
                                                h1: ({ node, ...props }) => <Typography variant="h5" {...props} sx={{ mt: 2, mb: 1 }} />,
                                                h2: ({ node, ...props }) => <Typography variant="h6" {...props} sx={{ mt: 2, mb: 1 }} />,
                                                code: ({ node, inline, className, children, ...props }) => {
                                                    return !inline ? (
                                                        <Box
                                                            component="pre"
                                                            sx={{
                                                                backgroundColor: (theme) => theme.palette.grey[200],
                                                                padding: 1,
                                                                borderRadius: 1,
                                                                overflowX: 'auto',
                                                                mb: 2,
                                                            }}
                                                        >
                                                            <code {...props}>{children}</code>
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            component="code"
                                                            sx={{
                                                                backgroundColor: (theme) => theme.palette.grey[200],
                                                                padding: '2px 4px',
                                                                borderRadius: 1,
                                                                fontFamily: 'monospace',
                                                            }}
                                                        >
                                                            {children}
                                                        </Typography>
                                                    );
                                                },
                                                a: ({ node, href, children, ...props }) => (
                                                    <Link href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                                        {children}
                                                    </Link>
                                                ),
                                                img: ({ node, src, alt, ...props }) => (
                                                    <Box
                                                        component="img"
                                                        src={src}
                                                        alt={alt}
                                                        sx={{ maxWidth: "100%", borderRadius: 1 }}
                                                        {...props}
                                                    />
                                                ),
                                            }}
                                        >
                                            {release.body}
                                        </ReactMarkdown>
                                    ) : (
                                        <Typography>No description provided.</Typography>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Box>
        </>
    );
};

export default Changelog;
